import { VoidFunc1 } from '@cgs/shared';
import {
  clamp,
  Compatibility,
  EventDispatcher,
  EventStream,
  IStreamSubscription,
  Vector2,
  View,
} from '@cgs/wge';
import { ICanvasManager } from '../canvas/i_canvas_manager';
import { PageInfo } from '../canvas/page_info';
import { ICoordinateSystemInfoProvider } from '../services/coordinate_system_info_provider';
import { IScaleCalculator, RectangleProvider } from './i_scale_calculator';
import { IScaleEntry } from './i_scale_entry';
import { ScaleInfo } from './scale_info';
import { Stretch } from './stretch';
import { TemplateApplication } from '@cgs/wge';
import { Rectangle } from './rectangle';

export class ScaleCalculator implements IScaleCalculator {
  private _coordinateSystemProvider: ICoordinateSystemInfoProvider;
  private _canvasManager: ICanvasManager;
  private _gameView: View;
  private _dstRectProvider: RectangleProvider;
  private _scaleEntries: IScaleEntry[];
  private _scaleChangedDispatcher: EventDispatcher<ScaleInfo> = new EventDispatcher<ScaleInfo>();
  private _pageInfo: PageInfo;
  private _sizeChangedSub: IStreamSubscription | null = null;
  private _stretch: Stretch;

  private get _scaleChanged(): EventStream<ScaleInfo> {
    return this._scaleChangedDispatcher.eventStream;
  }

  constructor(
    coordinateSystemProvider: ICoordinateSystemInfoProvider,
    canvasManager: ICanvasManager,
    gameView: View
  ) {
    this._coordinateSystemProvider = coordinateSystemProvider;
    this._canvasManager = canvasManager;
    this._gameView = gameView;
  }

  public async initialize(
    scaleEntries: IScaleEntry[],
    dstRect: Rectangle,
    stretch?: Stretch
  ): Promise<void> {
    return this.initializeDynamic(scaleEntries, () => dstRect, stretch ?? Stretch.Uniform);
  }

  public async initializeDynamic(
    scaleEntries: IScaleEntry[],
    dstRectProvider: RectangleProvider,
    stretch?: Stretch
  ): Promise<void> {
    this._scaleEntries = scaleEntries;
    this._dstRectProvider = dstRectProvider;
    this._stretch = stretch ?? Stretch.Uniform;
    this._sizeChangedSub = this._gameView.sizeChanged.listen(() => {
      Promise.resolve().then(() => this.invalidate());
      this.invalidate();
    });
    await this.invalidate();
  }

  private _calculateScale(
    pageInfo: PageInfo,
    canvasWidth: number,
    canvasHeight: number,
    coordinateSystemWidth: number,
    coordinateSystemHeight: number,
    dstRect: Rectangle
  ): Vector2 {
    const centerEntry = this._scaleEntries.find((e) => e.centerHorizontal);

    if (!centerEntry) {
      throw new Error('No center entry found');
    }

    const srcMinRect = new Rectangle(
      centerEntry.minBounds.left,
      centerEntry.minBounds.top,
      Compatibility.isPortrait ? centerEntry.minBounds.height : centerEntry.minBounds.width,
      Compatibility.isPortrait ? centerEntry.minBounds.width : centerEntry.minBounds.height
    );

    let srcMaxRect = new Rectangle(
      centerEntry.maxBounds.left,
      centerEntry.maxBounds.top,
      Compatibility.isPortrait ? centerEntry.maxBounds.height : centerEntry.maxBounds.width,
      Compatibility.isPortrait ? centerEntry.maxBounds.width : centerEntry.maxBounds.height
    );

    srcMaxRect = srcMaxRect!.boundingBox(srcMinRect!);

    let scale = new Vector2(1, 1);

    //Size of area that will be visible (in coordinate system).
    //It's equal to canvasSize clamped by min and max Rect.
    //Thus area size will be 1:1 to canvas size if it's within min and max rect.
    const srcWidth = clamp(
      (dstRect.width * canvasWidth) / coordinateSystemWidth,
      srcMinRect.width,
      srcMaxRect.width
    );
    //Calculate leftmost position of the area (in coordinate system).
    // const fractalX = (srcWidth - srcMinRect.width) / (srcMaxRect.width - srcMinRect.width);
    // const srcLeft = lerp(srcMinRect.left, srcMaxRect.left, isNaN(fractalX) ? 0.0 : fractalX);

    const srcHeight = clamp(
      (dstRect.height * canvasHeight) / coordinateSystemHeight,
      srcMinRect.height,
      srcMaxRect.height
    );
    // const fractalY = (srcHeight - srcMinRect.height) / (srcMaxRect.height - srcMinRect.height);
    // const srcTop = lerp(srcMinRect.top, srcMaxRect.top, isNaN(fractalY) ? 0.0 : fractalY);

    //Aspect ratio of area that will be visible
    const srcAspect = srcWidth / srcHeight;

    //Aspect ratio of coordinate system size
    const coordinateSystemAspect = coordinateSystemWidth / coordinateSystemHeight;

    const dstWidthPixels = (dstRect.width / coordinateSystemWidth) * canvasWidth;
    const dstHeightPixels = (dstRect.height / coordinateSystemHeight) * canvasHeight;
    const dstAspectPixels = dstWidthPixels / dstHeightPixels;

    //Compare canvas (pixels size) and visible area (in coordinate system) aspects to determinate whether blank spaces will be on left/right or top/bottom sides
    if (
      (this._stretch === Stretch.Uniform && dstAspectPixels > srcAspect) ||
      (this._stretch === Stretch.UniformToFit && dstAspectPixels < srcAspect)
    ) {
      //Scale by Y to fill exactly target area height (targetHeight)
      const scaleY = dstRect.height / srcHeight;
      //We already know height of visible area in pixels. It's canvasHeight, thus to uniform it we need to calculate width of target area in pixels keeping aspect ratio in coordinate system.
      const widthToKeepAspect = coordinateSystemAspect * canvasHeight;
      //Scale X just to keep aspect ratio because we will have blank spaces on sides anyway.
      scale = new Vector2((widthToKeepAspect * scaleY) / canvasWidth, scaleY);

      // const newSrcWidth = clamp(dstRect.width / scale.x, srcMinRect.width, srcMaxRect.width);
      // const newFractalX = (newSrcWidth - srcMinRect.width) / (srcMaxRect.width - srcMinRect.width);
      // const newSrcLeft = lerp(
      //   srcMinRect.left,
      //   srcMaxRect.left,
      //   isNaN(newFractalX) ? 0.0 : newFractalX
      // );
    } else {
      const scaleX = dstRect.width / srcWidth;
      const heightToKeepAspect = canvasWidth / coordinateSystemAspect;
      scale = new Vector2(scaleX, (heightToKeepAspect * scaleX) / canvasHeight);

      // const newSrcHeight = clamp(dstRect.height / scale.y, srcMinRect.height, srcMaxRect.height);
      // const newFractalY =
      //   (newSrcHeight - srcMinRect.height) / (srcMaxRect.height - srcMinRect.height);
      // const newSrcTop = lerp(
      //   srcMinRect.top,
      //   srcMaxRect.top,
      //   isNaN(newFractalY) ? 0.0 : newFractalY
      // );
    }
    return scale;
  }

  public async invalidate(): Promise<void> {
    this._pageInfo = await this._canvasManager.getPageInfo();
    this._onScaleChanged();
  }

  private _onScaleChanged(): void {
    const scaleInfo = this._updateScale();
    if (scaleInfo) {
      TemplateApplication.scale = scaleInfo.scale;
      this._scaleChangedDispatcher.dispatchEvent(scaleInfo);
    }
  }

  private _updateScale(): ScaleInfo | null {
    if (this._pageInfo && this._sizeChangedSub) {
      const coordinateSystem = this._coordinateSystemProvider.coordinateSystem;
      let dstRect = this._dstRectProvider();
      const clientViewport = new Rectangle(
        0.0,
        0.0,
        coordinateSystem.width,
        (this._pageInfo.clientHeight / this._gameView.height) * coordinateSystem.height
      );
      dstRect = dstRect.intersection(clientViewport);
      const scale = this._calculateScale(
        this._pageInfo,
        this._gameView.width,
        this._gameView.height,
        coordinateSystem.width,
        coordinateSystem.height,
        dstRect
      );
      return new ScaleInfo(scale, new Vector2(0, 0));
    }
    return null;
  }

  public stop(): void {
    this._sizeChangedSub?.cancel();
    this._sizeChangedSub = null;
    // this._dstRectProvider = null;
  }

  public reset(): void {
    this._scaleChangedDispatcher.dispatchEvent(new ScaleInfo(new Vector2(1, 1), new Vector2(0, 0)));
  }

  public addScaleChangedListener(listener: VoidFunc1<ScaleInfo>): IStreamSubscription {
    const sub = this._scaleChanged.listen(listener);
    this._onScaleChanged();
    return sub;
  }
}
